import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { APP_VERSION, parseReleaseNotes } from '../version'

/**
 * Which build you are looking at, and what changed in it.
 *
 * Two jobs, and the second is the one that matters. A version alone answers "did my fix deploy";
 * the changelog answers "why does this look different from yesterday", which is otherwise
 * unanswerable from the outside — and on a site that publishes numbers people may bet against, a
 * silent change to how those numbers are produced is exactly the thing to be able to point at.
 *
 * The version renders from a build-time constant, so it is on screen before any request is made and
 * cannot be taken down by a parse bug — which is what happened three releases running. The notes
 * body is fetched only when the panel is opened: most visits never ask.
 */
export function VersionBadge() {
  const [open, setOpen] = useState(false)
  const { data, isPending, error } = useQuery({
    queryKey: ['release-notes'],
    queryFn: async () => {
      const response = await fetch('/RELEASE_NOTES.md', { cache: 'no-store' })
      if (!response.ok) throw new Error(`${response.status}`)
      return response.text()
    },
    // Immutable for the life of a build, so there is nothing to revalidate against.
    staleTime: Infinity,
    retry: false,
    enabled: open,
  })

  const releases = data ? parseReleaseNotes(data) : []
  // The constant is the source of truth. The parsed heading is the fallback for a dev tree built
  // without generated notes, where the constant is empty.
  const current = APP_VERSION || releases[0]?.version

  return (
    <div className="mt-3 border-t border-line pt-3">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        className="text-xs text-ink-faint hover:text-ink-soft"
      >
        Dota Oracle <span className="tabular">{current ?? 'dev'}</span> · what changed
      </button>

      {open ? (
        <div className="mt-3 max-h-80 overflow-y-auto rounded border border-line bg-surface p-3">
          {isPending ? <p className="text-xs text-ink-faint">Loading…</p> : null}
          {error ? (
            <p className="text-xs text-ink-faint">
              No release notes in this build. They are generated at release time, so a locally built
              image has none.
            </p>
          ) : null}
          {!isPending && !error && releases.length === 0 ? (
            <p className="text-xs text-ink-faint">The notes file is present but has no entries.</p>
          ) : null}

          {releases.map((release) => (
            <section key={release.version} className="mb-3 last:mb-0">
              <h4 className="tabular text-xs font-medium text-ink">
                {release.version}
                {release.date ? (
                  <span className="ml-2 font-normal text-ink-faint">{release.date}</span>
                ) : null}
              </h4>
              <ul className="mt-1 space-y-0.5">
                {release.changes.map((change, index) => (
                  <li key={index} className="text-xs leading-relaxed text-ink-soft">
                    {change}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  )
}
